/**
 * @file src/shell/Header.js
 * HEADER — dải ngang ở ĐỈNH: nút "quay lại", tiêu đề bài học, và ba nút cố định
 * Bút · Bảng trắng · Bài tập (icon lấy nguyên từ file thiết kế "Thiết kế lại tab bar
 * UI", phương án 1A, xem icons.js).
 *
 * BA NÚT CÙNG MỘT TRỌNG SỐ: cùng cỡ, cùng viền, cùng độ đậm chữ, đứng cạnh nhau như ba
 * viên rời. Từng có một bản chia nhóm theo phương án 1A của file thiết kế — Bút+Bảng
 * trắng chung một khay xám nhạt kiểu segmented control, Bài tập tách riêng và to hơn để
 * "nặng" hơn hai nút kia — nhưng review yêu cầu đổi lại như cũ, nên đã bỏ khay đó và
 * cho cả ba về đúng một kiểu. ĐỪNG chia nhóm lại mà không có yêu cầu mới.
 *
 * LUẬT BẤT DI BẤT DỊCH của src/shell/ vẫn áp dụng: file này không biết tên tiết nào.
 * Tiêu đề không đọc từ config.js của tiết — nó đọc từ content/index/<môn>.json (tham
 * số `info`, CÙNG nguồn mà LoadingScreen và InfoPanel đã dùng), vì đó là nơi DUY NHẤT
 * admin quản lý tiêu đề (một chỗ sửa, không lệch giữa panel/trang cha/catalog/header).
 * "khi config.js của mỗi tiết riêng" trong yêu cầu vì vậy được hiểu là "mỗi tiết có dữ
 * liệu riêng" — Header đọc đúng info của TIẾT ĐANG CHẠY, không phải đọc từ config.js.
 *
 * Nút Bút/Bảng trắng tự đổi trạng thái BẬT/TẮT hình ảnh của riêng nó khi được BẤM, và
 * gọi callback tuỳ chọn (`onTogglePen`/`onToggleWhiteboard`) nếu được cấp. PenLayer.js
 * và Whiteboard.js là hai nơi cấp callback đó (xem Shell.js). Cả hai đều có đường ĐÓNG
 * khác ngoài bấm lại chính nó, nên cả hai đều cần một cửa ngõ NGƯỢC
 * (`setPenActive()`/`setWhiteboardActive()`): gọi khi thứ tương ứng bị đóng từ nơi khác,
 * để nút ở Header tắt sáng theo mà KHÔNG phát lại callback (tránh gọi onTogglePen/
 * onToggleWhiteboard hai lần cho một lần đóng).
 *   Bảng trắng: nút × ngay trong panel.
 *   Bút: trước đây KHÔNG có đường nào khác (chú thích cũ ở đây nói vậy nên không có
 *     setPenActive) — nay CÓ: vào chế độ hẹp/mobile thì Shell tắt cả Bút và Bảng trắng
 *     vì hai nút đó bị ẩn ở mobile. Thiếu setPenActive thì `penOn` của Header lệch với
 *     thực tế: quay máy về ngang, nút Bút hiện lại và vẫn sáng dù lớp vẽ đã tắt.
 *
 * Nút Bài tập giờ hoạt động Y HỆT Bảng trắng (không phải một lần bấm gọi hành động
 * như trước): tự đổi trạng thái BẬT/TẮT, gọi `onToggleExercise(on)`, và có
 * `setExerciseActive()` để đồng bộ NGƯỢC khi panel tự đóng (nút × của nó, hoặc khi
 * người dùng mở Thông tin/Cài đặt — ba panel đó dùng chung một chỗ, xem Shell.js).
 *
 * Nút "quay lại" cũng vậy: KHÔNG đụng vào bridge.js (giao thức postMessage đã đóng
 * băng, "CHỈ THÊM không đổi" — thêm một lệnh mới ra ngoài là quyết định cần chốt riêng,
 * không tự quyết trong lúc làm UI). Mặc định dùng `history.back()` nếu có lịch sử để
 * lùi; `onBack` là cửa ngõ để boot.js nối vào một hành vi thật sau này.
 */

import { icon } from './icons.js';

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {Record<string, any>} o.info   Thông tin bài giảng từ content/index (có title)
 * @param {() => string} o.lang
 * @param {() => void} [o.onBack]
 * @param {(on: boolean) => void} [o.onTogglePen]
 * @param {(on: boolean) => void} [o.onToggleWhiteboard]
 * @param {(on: boolean) => void} [o.onToggleExercise]
 */
export function createHeader(o) {
  const el = document.createElement('header');
  el.className = 'ol-header';
  el.innerHTML = `
    <button type="button" class="ol-header__back">
      <span class="ol-icon">${icon('back')}</span><span class="ol-header__back-label"></span>
    </button>
    <span class="ol-header__titlewrap">
      <span class="ol-header__title"></span>
      <span class="ol-header__pill"></span>
    </span>
    <span class="ol-header__pen-slot" hidden></span>
    <div class="ol-header__actions">
      <button type="button" class="ol-header__action" data-action="pen">
        <span class="ol-icon">${icon('pen')}</span><span class="ol-header__label"></span>
      </button>
      <button type="button" class="ol-header__action" data-action="whiteboard">
        <span class="ol-icon">${icon('penSquare')}</span><span class="ol-header__label"></span>
      </button>
      <button type="button" class="ol-header__action" data-action="exercise">
        <span class="ol-icon">${icon('notebookSquare')}</span><span class="ol-header__label"></span>
      </button>
    </div>`;
  // prepend, KHÔNG appendChild: .ol-stage đã là con đầu tiên của root từ trước khi
  // mountShell() chạy (xem boot.js). .ol-root là flex column nên thứ tự DOM = thứ tự
  // hiển thị trên↔dưới — header phải chen lên TRƯỚC .ol-stage trong DOM để nằm phía
  // trên nó, không phải chỉ "trông như ở trên" bằng CSS position/order (prepend giữ
  // đúng thứ tự đọc cho trình đọc màn hình: header trước nội dung).
  o.root.prepend(el);

  const titleEl = /** @type {HTMLElement} */ (el.querySelector('.ol-header__title'));
  const pillEl = /** @type {HTMLElement} */ (el.querySelector('.ol-header__pill'));
  const penSlotEl = /** @type {HTMLElement} */ (el.querySelector('.ol-header__pen-slot'));
  const backBtn = /** @type {HTMLButtonElement} */ (el.querySelector('.ol-header__back'));
  const backLabelEl = /** @type {HTMLElement} */ (el.querySelector('.ol-header__back-label'));
  const penBtn = /** @type {HTMLButtonElement} */ (el.querySelector('[data-action="pen"]'));
  const wbBtn = /** @type {HTMLButtonElement} */ (el.querySelector('[data-action="whiteboard"]'));
  const exBtn = /** @type {HTMLButtonElement} */ (el.querySelector('[data-action="exercise"]'));

  // Khai TRƯỚC render() — render() đọc penOn ngay lần gọi đầu tiên (ẩn/hiện pill),
  // khai SAU thì lần gọi đó rơi vào vùng chết tạm thời (TDZ) của `let`, ném lỗi.
  let penOn = false;
  let wbOn = false;
  let exOn = false;

  /** @param {unknown} v @returns {string} Pick theo ngôn ngữ, rơi về vi — CÙNG công
   * thức mà InfoPanel/LoadingScreen đã dùng cho field đa ngôn ngữ của content/index. */
  function pick(v) {
    if (v && typeof v === 'object') {
      const rec = /** @type {Record<string, string>} */ (v);
      return rec[o.lang()] ?? rec.vi ?? '';
    }
    return typeof v === 'string' ? v : '';
  }

  function render() {
    titleEl.textContent = String(pick(o.info?.title));

    // Pill "Địa lí 10" — subjectName (content/index/<môn>.json, top-level, merge vào
    // info ở main.js/build-lesson.mjs) + grade (nằm sẵn trong info của TIẾT). Ẩn hẳn
    // nếu tiết/build cũ chưa có subjectName (không để lại pill rỗng lơ lửng).
    const subjectName = pick(o.info?.subjectName);
    const grade = o.info?.grade;
    const pillText = [subjectName, grade].filter(Boolean).join(' ');
    pillEl.textContent = pillText;
    pillEl.hidden = !pillText;

    const label = (/** @type {HTMLButtonElement} */ btn, /** @type {string} */ key) => {
      const text = o.t(key);
      btn.title = text;
      btn.setAttribute('aria-label', text);
      /** @type {HTMLElement} */ (btn.querySelector('.ol-header__label')).textContent = text;
    };
    label(penBtn, 'header.pen');
    label(wbBtn, 'header.whiteboard');
    label(exBtn, 'header.exercise');
    const backText = o.t('header.back');
    backBtn.title = backText;
    backBtn.setAttribute('aria-label', backText);
    backLabelEl.textContent = backText;
  }
  render();

  const onBackClick = () => {
    if (o.onBack) {
      o.onBack();
      return;
    }
    // Không có callback: lùi lịch sử trình duyệt nếu có gì để lùi về, không thì im.
    // KHÔNG gọi bridge.js ở đây — xem chú thích đầu file.
    // window.history (không phải `history` trần) — eslint.config.js chỉ allowlist
    // `window`, không allowlist `history` như một global riêng.
    if (window.history.length > 1) window.history.back();
  };
  backBtn.addEventListener('click', onBackClick);

  const onPenClick = () => {
    penOn = !penOn;
    penBtn.classList.toggle('is-active', penOn);
    // Slot bảng màu đứng RIÊNG, ngay trước nhóm nút hành động (KHÔNG dùng chung ô với
    // pill "Địa lí 10" nữa — bản trước gộp chung ô với pill trong titlewrap khiến bảng
    // màu "bay" sang tận cạnh tiêu đề, xa hẳn nút Bút vừa bấm, bị nhận xét đúng vậy).
    penSlotEl.hidden = !penOn;
    o.onTogglePen?.(penOn);
  };
  const onWbClick = () => {
    wbOn = !wbOn;
    wbBtn.classList.toggle('is-active', wbOn);
    o.onToggleWhiteboard?.(wbOn);
  };
  const onExClick = () => {
    exOn = !exOn;
    exBtn.classList.toggle('is-active', exOn);
    o.onToggleExercise?.(exOn);
  };
  penBtn.addEventListener('click', onPenClick);
  wbBtn.addEventListener('click', onWbClick);
  exBtn.addEventListener('click', onExClick);

  return {
    el,
    retranslate: render,
    /** @returns {HTMLElement} Ô trống đứng NGAY TRƯỚC nhóm nút hành động (Bút/Bảng
     * trắng/Bài tập) — PenLayer.js CHUYỂN THẲNG node bảng màu của nó vào đây lúc bật
     * bút lần đầu, thay vì nổi lơ lửng ở góc sân khấu (bản trước, từng bị nhận xét
     * "chèn" lên cột công cụ). Đứng cạnh nhóm nút, KHÔNG chung ô với pill "Địa lí 10"
     * trong titlewrap (từng thử, bị nhận xét bảng màu "bay" xa khỏi nút Bút vừa bấm).
     * Header đứng NGOÀI .ol-stage (con của .ol-root) nên PenLayer không tự có node
     * này, phải cấp qua đây. */
    getPenSlot: () => penSlotEl,
    /** @param {boolean} on Đồng bộ NGƯỢC khi lớp vẽ bị tắt từ nơi khác — hiện là lúc vào
     * chế độ hẹp/mobile (Shell.setCompact tắt Bút vì nút bị ẩn ở đó). Phải xử lý cả ô
     * bảng màu và pill môn+lớp y như onPenClick, không thì tắt Bút xong bảng màu vẫn
     * nằm lại trong header. */
    setPenActive(on) {
      penOn = on;
      penBtn.classList.toggle('is-active', on);
      pillEl.hidden = pillEl.textContent === '' || on;
      penSlotEl.hidden = !on;
    },
    /** @param {boolean} on Đồng bộ NGƯỢC khi panel Bảng trắng tự đóng (nút × của nó). */
    setWhiteboardActive(on) {
      wbOn = on;
      wbBtn.classList.toggle('is-active', on);
    },
    /** @param {boolean} on Đồng bộ NGƯỢC khi panel Bài tập tự đóng hoặc bị đóng vì mở Thông tin/Cài đặt. */
    setExerciseActive(on) {
      exOn = on;
      exBtn.classList.toggle('is-active', on);
    },
    dispose() {
      backBtn.removeEventListener('click', onBackClick);
      penBtn.removeEventListener('click', onPenClick);
      wbBtn.removeEventListener('click', onWbClick);
      exBtn.removeEventListener('click', onExClick);
      el.remove();
    },
  };
}
